import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {Object} OfferBundlesRefs
 * @property {HTMLButtonElement} ctaButton
 * @property {HTMLElement} ctaPrice
 * @property {HTMLElement} errorMessage
 */

/**
 * @extends {Component<OfferBundlesRefs>}
 */
class OfferBundles extends Component {
  requiredRefs = ['ctaButton', 'ctaPrice', 'errorMessage'];

  /**
   * Updates the CTA price/availability when a different offer is selected.
   * @param {Event} event
   */
  handleSelect(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);

    this.refs.ctaPrice.textContent = input.dataset.price ?? '';
    this.refs.ctaButton.disabled = input.dataset.available === 'false';
  }

  /**
   * Adds every component of the selected offer to the cart in a single
   * request, then notifies the native cart drawer via the standard
   * cart-lines-update event so it opens and refreshes on its own.
   * @param {Event} event
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.offer-bundles__radio:checked')
    );
    if (!checked) return;

    const items = JSON.parse(checked.dataset.items ?? '[]');
    if (items.length === 0) return;

    this.refs.ctaButton.disabled = true;
    this.refs.errorMessage.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      }).then((res) => res.json());

      if (response.status) throw new Error(response.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.json`).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'offer-bundles', sourceId: this.id, didError: false },
      });

      this.#showAdded();
    } catch (error) {
      deferred.reject(error);
      this.refs.errorMessage.hidden = false;
      this.refs.errorMessage.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      this.refs.ctaButton.disabled = false;
    }
  }

  #showAdded() {
    const { ctaButton } = this.refs;
    ctaButton.dataset.added = 'true';

    setTimeout(() => {
      ctaButton.removeAttribute('data-added');
    }, 1500);
  }
}

if (!customElements.get('offer-bundles')) {
  customElements.define('offer-bundles', OfferBundles);
}
